import { Alert, Badge, Button, Card, Checkbox, FileInput, Group, Modal, SimpleGrid, Stack, Table, Tabs, Text, ThemeIcon, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconCheck, IconDownload, IconFileSpreadsheet, IconFileZip, IconMap2, IconPencil, IconPlayerPlay, IconSearch, IconTableImport,
} from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useNavigate } from "react-router";

import { downloadFile, http } from "../../api/http";
import { useCan } from "../../auth/user";
import { DataTable } from "../../components/DataTable";
import { fmtDateTime, PageHeader, StatusBadge } from "../../components/ui";
import { useT } from "../../i18n/script";
import { NewOrchardForm } from "../orchard/NewOrchardForm";

interface ImportType {
  code: string; name: string; description: string; kind: "table" | "vector" | "zip"; legal: string;
  columns: { key: string; label: string; type: string; required: boolean; help: string; options: string[]; codelist: string | null }[];
}
interface Result {
  id: number; status: string; message: string; total: number; ok: number; errors: number; warnings: number; unknown_columns: string[];
  rows: { red: number; fajl?: string; status: string; poruka: string; action?: string; id?: number }[];
}

const ICON = { table: <IconFileSpreadsheet />, vector: <IconMap2 />, zip: <IconFileZip /> };
const ACCEPT = { table: ".xlsx,.csv", vector: ".geojson,.json,.gpkg,.kml,.zip", zip: ".zip" };

/** Uvoz podataka kroz aplikaciju (Administracija): obrasci, provjera bez upisa, uvoz, izvještaj po redu i istorija. */
export default function ImportPage() {
  return (
    <Stack>
      <PageHeader title="Uvoz podataka" description="Preuzmite obrazac, popunite ga i pokrenite provjeru — dobićete izvještaj po redu bez ikakvog upisa. Uvoz koristi ista pravila kao ručni unos (obavezna polja, karenca, nadležnost po opštini) i evidentira se u istoriji aktivnosti; original fajla čuva se u DMS-u. Uvoz za jedno gazdinstvo ili voćnjak pokrenite iz njegovog kartona — BPG i voćnjak se tada popunjavaju sami." />
      <ImportPanel />
    </Stack>
  );
}

/**
 * Uvoz u kontekstu: `context` (npr. {bpg} iz kartona gazdinstva ili {bpg, orchard, orchard_id} iz kartona voćnjaka)
 * popunjava prazne kolone i ograničava uvoz na to gazdinstvo; `only` sužava ponuđene tipove.
 */
/** Isti podaci kroz aplikaciju: šifra uvoza → obrazac u kartonu voćnjaka (`?novi=`) ili novi voćnjak. */
const MANUAL: Record<string, { novi?: string; label: string; perm: string }> = {
  vocnjaci: { label: "Novi voćnjak", perm: "orchards:write" },
  vocnjaci_gis: { label: "Novi voćnjak", perm: "orchards:write" },
  tretmani: { novi: "zastita", label: "Upiši tretman", perm: "diary:write" },
  dubrenje: { novi: "dubrenje", label: "Upiši đubrenje", perm: "diary:write" },
  berba: { novi: "berba", label: "Upiši berbu", perm: "diary:write" },
  fenologija: { novi: "fenologija", label: "Upiši zapažanje", perm: "phenology:write" },
  fotografije_zip: { novi: "fotografije", label: "Dodaj fotografije", perm: "photos:write" },
  stabla: { novi: "stabla", label: "Stabla u kartonu", perm: "orchards:read" },
};

export function ImportPanel({ context, only, holdingId, orchards }: { context?: Record<string, string | number>; only?: string[];
  holdingId?: number; orchards?: { id: number; name: string }[] }) {
  const { t } = useT();
  const can = useCan();
  const nav = useNavigate();
  const [newOrchard, setNewOrchard] = useState(false);
  const [pick, setPick] = useState<string | null>(null); // `novi` za koji se bira voćnjak
  const manual = (code: string) => {
    const m = MANUAL[code];
    if (!m || !can(m.perm)) return null;
    if (!m.novi) return holdingId ? () => setNewOrchard(true) : null;
    const oid = context?.orchard_id;
    if (oid) return () => nav(`/vocnjaci/${oid}?novi=${m.novi}`);
    if (orchards?.length === 1) return () => nav(`/vocnjaci/${orchards[0].id}?novi=${m.novi}`);
    if (orchards?.length) return () => setPick(m.novi!);
    return null;
  };
  const qc = useQueryClient();
  const typesQ = useQuery({ queryKey: ["import-types"], queryFn: () => http.get<ImportType[]>("/api/v1/imports/types") });
  const types = { data: typesQ.data?.filter((x) => !only || only.includes(x.code)) };
  const log = useQuery({ queryKey: ["import-log"], queryFn: () => http.get<Record<string, unknown>[]>("/api/v1/imports/log", { mine: true }) });
  const [sel, setSel] = useState<ImportType | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [partial, setPartial] = useState(false);
  const [busy, setBusy] = useState<"dry" | "run" | null>(null);
  const [res, setRes] = useState<Result | null>(null);
  const [checked, setChecked] = useState<string | null>(null); // fajl koji je prošao provjeru

  function open(tp: ImportType) { setSel(tp); setFile(null); setRes(null); setChecked(null); setPartial(false); }

  async function run(dry: boolean) {
    if (!sel || !file) return;
    setBusy(dry ? "dry" : "run");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", String(dry));
      fd.append("allow_partial", String(partial));
      if (context) fd.append("context", JSON.stringify(context));
      const r = await http.form<Result>(`/api/v1/imports/${sel.code}`, fd);
      setRes(r);
      if (dry) setChecked(`${file.name}:${file.size}:${file.lastModified}`);
      notifications.show({ color: r.errors ? (r.status === "odbijeno" ? "red" : "orange") : "green", message: t(r.message) });
      qc.invalidateQueries({ queryKey: ["import-log"] });
    } catch (e) {
      notifications.show({ color: "red", message: String(e) });
    } finally { setBusy(null); }
  }
  const fileKey = file ? `${file.name}:${file.size}:${file.lastModified}` : null;

  return (
    <Stack>
      {context && <Text size="sm" c="dimmed">{t("Kolone")} {Object.keys(context).filter((k) => k !== "orchard_id").map((k) => <b key={k}>{k === "bpg" ? "BPG" : t("Voćnjak")} </b>)}{t("se popunjavaju automatski i mogu se izostaviti iz obrasca; redovi drugog gazdinstva se odbijaju.")}</Text>}
      <Tabs defaultValue="tipovi">
        <Tabs.List>
          <Tabs.Tab value="tipovi" leftSection={<IconTableImport size={14} />}>{t("Novi uvoz")}</Tabs.Tab>
          <Tabs.Tab value="istorija">{t("Moji uvozi")}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="tipovi" pt="md">
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
            {(types.data ?? []).map((tp) => (
              <Card key={tp.code} withBorder padding="lg">
                <Group wrap="nowrap" align="flex-start" mb="xs">
                  <ThemeIcon size={40} variant="light">{ICON[tp.kind]}</ThemeIcon>
                  <div>
                    <Text fw={600}>{t(tp.name)}</Text>
                    <Text size="xs" c="dimmed">{t(tp.description)}</Text>
                  </div>
                </Group>
                {tp.legal && <Badge size="xs" variant="outline" color="gray" mb="xs">{tp.legal}</Badge>}
                <Group gap="xs" mt="auto">
                  {tp.kind === "table" && (
                    <Button size="xs" variant="light" leftSection={<IconDownload size={14} />}
                      onClick={() => downloadFile(`/api/v1/imports/templates/${tp.code}.xlsx`).catch((e) => notifications.show({ color: "red", message: String(e) }))}>
                      {t("Obrazac (Excel)")}</Button>
                  )}
                  <Button size="xs" leftSection={<IconTableImport size={14} />} onClick={() => open(tp)}>{t("Uvezi")}</Button>
                  {manual(tp.code) && (
                    <Button size="xs" variant="subtle" leftSection={<IconPencil size={14} />} onClick={manual(tp.code)!}>
                      {t(MANUAL[tp.code].label)}</Button>
                  )}
                </Group>
              </Card>
            ))}
          </SimpleGrid>
          {types.data && !types.data.length && <Alert color="yellow">{t("Nemate ovlašćenje ni za jedan tip uvoza.")}</Alert>}
        </Tabs.Panel>
        <Tabs.Panel value="istorija" pt="md">
          <DataTable rows={log.data ?? []} exportName="moji_uvozi" columns={[
            { key: "created_at", label: "Vrijeme", render: (r) => fmtDateTime(r.created_at) }, { key: "import_type", label: "Tip" },
            { key: "filename", label: "Fajl" }, { key: "status", label: "Status", render: (r) => <StatusBadge status={String(r.status)} /> },
            { key: "rows_total", label: "Redova", type: "integer" }, { key: "rows_ok", label: "Uspješno", type: "integer" },
            { key: "rows_error", label: "Grešaka", type: "integer" }, { key: "warnings", label: "Upozorenja", type: "integer" },
            { key: "x", label: "", render: (r) => <Button size="compact-xs" variant="subtle" leftSection={<IconDownload size={12} />}
              onClick={() => downloadFile(`/api/v1/imports/log/${r.id}/report.xlsx`)}>{t("Izvještaj")}</Button> },
          ]} />
        </Tabs.Panel>
      </Tabs>

      {holdingId && (
        <Modal opened={newOrchard} onClose={() => setNewOrchard(false)} size="xl" title={t("Novi voćnjak")}>
          {newOrchard && <NewOrchardForm holdingId={holdingId} onDone={() => setNewOrchard(false)} />}
        </Modal>
      )}
      <Modal opened={!!pick} onClose={() => setPick(null)} title={t("Izaberite voćnjak")}>
        <Stack gap={6}>
          {(orchards ?? []).map((o) => (
            <Button key={o.id} variant="light" justify="space-between" rightSection={<IconPencil size={14} />}
              onClick={() => nav(`/vocnjaci/${o.id}?novi=${pick}`)}>{o.name}</Button>
          ))}
        </Stack>
      </Modal>
      <Modal opened={!!sel} onClose={() => setSel(null)} size="xl" title={sel ? t(sel.name) : ""}>
        {sel && (
          <Stack>
            {sel.columns.length > 0 && (
              <details>
                <summary style={{ cursor: "pointer" }}><Text span size="sm" fw={500}>{t("Kolone obrasca")} ({sel.columns.length})</Text></summary>
                <Table fz="xs" mt="xs" verticalSpacing={2}>
                  <Table.Tbody>
                    {sel.columns.map((c) => (
                      <Table.Tr key={c.key}><Table.Td fw={c.required ? 700 : 400}>{t(c.label)}{c.required ? " *" : ""}</Table.Td>
                        <Table.Td c="dimmed">{c.key}</Table.Td><Table.Td>{t(c.help)}{c.options.length ? ` (${c.options.join(", ")})` : ""}</Table.Td></Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </details>
            )}
            <FileInput label={t("Fajl")} accept={ACCEPT[sel.kind]} value={file} clearable onChange={(f) => { setFile(f); setRes(null); }}
              description={sel.kind === "table" ? t("Excel (.xlsx) ili CSV (; ili ,)") : sel.kind === "vector"
                ? t("GeoJSON, GeoPackage, KML ili Shapefile u ZIP-u; WGS84, UTM 33/34N, EPSG:8677/8678") : t("ZIP sa fotografijama ŠIFRA_GGGGMMDD.jpg")} />
            <Checkbox label={t("Djelimičan uvoz: upiši ispravne redove i kada neki redovi imaju grešku")} checked={partial}
              onChange={(e) => setPartial(e.currentTarget.checked)} />
            <Group>
              <Button variant="light" leftSection={<IconSearch size={16} />} disabled={!file} loading={busy === "dry"} onClick={() => run(true)}>{t("1. Provjera (bez upisa)")}</Button>
              <Button leftSection={<IconPlayerPlay size={16} />} disabled={!file || checked !== fileKey} loading={busy === "run"} onClick={() => run(false)}>{t("2. Uvoz")}</Button>
              {file && checked !== fileKey && <Text size="xs" c="dimmed">{t("Uvoz je moguć nakon provjere ovog fajla.")}</Text>}
            </Group>
            {res && (
              <Stack gap="xs">
                <Alert color={res.status === "uvezeno" ? "green" : res.errors ? (res.status === "provjera" ? "orange" : "red") : "blue"}
                  icon={res.errors ? undefined : <IconCheck />} title={<Group gap="xs"><StatusBadge status={res.status} />{t(res.message)}</Group>}>
                  {t("Redova")}: {res.total} · {t("ispravnih")}: {res.ok} · {t("sa greškom")}: {res.errors} · {t("upozorenja")}: {res.warnings}
                  {res.unknown_columns.length > 0 && <Text size="xs" mt={4}>{t("Zanemarene kolone")}: {res.unknown_columns.join(", ")}</Text>}
                </Alert>
                <Group justify="flex-end">
                  <Button size="xs" variant="subtle" leftSection={<IconDownload size={14} />} onClick={() => downloadFile(`/api/v1/imports/log/${res.id}/report.xlsx`)}>{t("Izvještaj (Excel)")}</Button>
                </Group>
                <DataTable rows={res.rows as unknown as Record<string, unknown>[]} maxHeight={360} dense columns={[
                  { key: "red", label: "Red", type: "integer", width: 50 }, ...(sel.kind === "zip" ? [{ key: "fajl", label: "Fajl" }] : []),
                  { key: "status", label: "Status", render: (r) => <StatusBadge status={String(r.status)} /> },
                  { key: "action", label: "Rezultat" }, { key: "poruka", label: "Poruka" },
                ]} />
              </Stack>
            )}
          </Stack>
        )}
      </Modal>
      <Title order={6} c="dimmed" fw={400}>{t("Napomena: provjera ne upisuje ništa; i provjera i uvoz se bilježe u istoriji uvoza i revizijskom tragu.")}</Title>
    </Stack>
  );
}
